import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import { filter, first, map, switchMap, tap } from 'rxjs/operators';
import { ApiResponse, ApiResponseStatusCode, convertToLocalDate, DateOnly, DICTIONARY_NAME_USSTATE, ProductTypeEnum, SURGEON, US_CODE, UserLastSignIn, UserProfile, UserProfileCountry, UserRepository } from '../core';
import { ProfileForm } from '../models';


/**
 * This service handles user profile.
 */
@Injectable({ providedIn: 'root' })
export class UserService {

	private readonly userRepo: UserRepository = inject(UserRepository);

	private readonly _currentUser$ = new BehaviorSubject<UserProfile | null>(null);
	private readonly _usStatesList$ = new BehaviorSubject<string[]>([]);

	private readonly CUSTOMER_CARE: string = 'CustomerCare';

	hasProduct = toSignal(forkJoin({ jps: this.isJps(), fitbone: this.isFitbone(), tlhex: this.isTlhex() }));


	/**
	* Get first name and last name of user profile.
	*/
	getUserName(): Observable<{ firstName: string, lastName: string }> {
		return this.getCurrentUser().pipe(map(user => ({ firstName: user.firstName, lastName: user.lastName })));
	}

	/**
	* Get country code of user profile.
	*/
	getUserCountry(): Observable<string> {
		return this.getCurrentUser().pipe(filter(user => !!user), map(user => user.country.code));
	}

	/**
	* Get language of user profile.
	*/
	getUserLanguage(): Observable<string | null> {
		return this.getCurrentUser().pipe(filter(user => !!user), map(user => user.language ?? null));
	}

	/**
	* Load user profile.
	*/
	loadUser(): Observable<UserProfile> {
		return this.getUserProfile().pipe(first(user => !!user), tap(user => this._currentUser$.next(user)));
	}

	/**
	* Get current user profile.
	*/
	getCurrentUser(): Observable<UserProfile> {
		return this._currentUser$.asObservable().pipe(first(user => !!user));
	}

	/**
	* Get United States list.
	*/
	getUsStatesList(): Observable<string[]> {
		if (this._usStatesList$.getValue()?.length) {
			return this._usStatesList$.asObservable().pipe(first());
		}
		return this.getUnitedStates().pipe(tap(list => this._usStatesList$.next(list)));
	}

	/**
	* Check if user has to accept legal documents.
	*/
	hasToAcceptLegalDocs(): Observable<boolean> {
		return this._currentUser$.asObservable().pipe(
			first(user => !!user),
			map(user => !!user.hasToAcceptLegalDocs)
		);
	}

	/**
	* Check if user is customer care.
	*/
	isCustomerCare(): Observable<boolean> {
		return this._currentUser$.asObservable().pipe(
			first(user => !!user && !!user.roles),
			map(user => !!user?.roles.includes(this.CUSTOMER_CARE))
		);
	}

	/**
	 * Check if input user is different than customer care.
	 */
	isCustomerCareOtherUser(usrId: string | null): Observable<boolean> {
		return this.isCustomerCare().pipe(
			map(isCC => isCC && this._currentUser$.value?.id != usrId)
		);
	}

	/**
	* Check if user has Platform Demo product.
	*/
	isPlatformDemo(): Observable<boolean> {
		return this._currentUser$.asObservable().pipe(
			first(user => !!user?.products),
			map(user => user!.products.some(p => p.type == ProductTypeEnum.Platform && p.demo))
		);
	}

	/**
	* Check if user has products available.
	*/
	hasProductsAvailable(): Observable<boolean> {
		return this._currentUser$.asObservable().pipe(
			first(user => !!user?.products),
			map(user => user!.products?.map(p => p.type) ?? []),
			map(types => types.some(t => t == ProductTypeEnum.JPS || t == ProductTypeEnum.Fitbone || t == ProductTypeEnum.TLHex))
		);
	}

	/**
	* Check if user has input product.
	*/
	isProduct(product: ProductTypeEnum): Observable<boolean> {
		return this._currentUser$.asObservable().pipe(
			first(user => !!user?.products),
			map(user => user!.products.some(p => p.type == product))
		);
	}

	/**
	* Check if user has Jps product.
	*/
	isJps(): Observable<boolean> {
		return this.isProduct(ProductTypeEnum.JPS);
	}

	/**
	* Check if user has Fitbone product.
	*/
	isFitbone(): Observable<boolean> {
		return this.isProduct(ProductTypeEnum.Fitbone);
	}

	/**
	* Check if user has Tlhex product.
	*/
	isTlhex(): Observable<boolean> {
		return this.isProduct(ProductTypeEnum.TLHex);
	}

	/**
	* Check if user has MyHexPlan product.
	*/
	isMyhexplan(): Observable<boolean> {
		return this.isProduct(ProductTypeEnum.MyHexPlan);;
	}

	/**
	* Get user guid by user name.
	*/
	getUserGuid(username: string): Observable<string> {
		return this.userRepo.getUserGuid(username).pipe(
			map(res => this.handleApiResponse(res))
		);
	}

	/**
	 * Get list of unites states.
	 */
	getUnitedStates(): Observable<string[]> {
		return this.userRepo.getDictionaries(DICTIONARY_NAME_USSTATE).pipe(
			map(res => this.handleApiResponse(res)),
			filter(list => list?.length === 1),
			map(list => list[0].values),
			map(list => list.map(state => state.otherInfo!))
		);
	}

	/**
	* Get edit form model from user profile.
	*/
	getProfileForm(): Observable<ProfileForm> {
		return this.getUserProfile().pipe(map(profile => this.profileFormMapper(profile)));
	}

	/**
	* Update user profile by form model.
	*/
	editProfileForm(form: ProfileForm): Observable<any> {
		const oldProfile = this._currentUser$.value;
		const newProfile = this.updateUserProfile(oldProfile!, form);
		return this.userRepo.editUser(newProfile).pipe(
			map(res => this.handleApiResponse(res))
		);
	}

	/**
	 * Get user last sign in. 
	 */
	getLastSignIn(): Observable<UserLastSignIn> {
		return this.getCurrentUser().pipe(
			switchMap(user => this.userRepo.getLastSignIn(user.id)),
			map(res => this.handleApiResponse(res))
		);
	}

	/**
	 * Get user languages.
	 */
	getLanguages(): Observable<string[]> {
		return this.userRepo.getLanguages().pipe(
			map(res => this.handleApiResponse(res))
		);
	}

	/**
	 * Change user language.
	 */
	changeLanguage(lang: string): Observable<void> {
		return this.userRepo.changeLanguage(lang).pipe(
			map(res => this.handleApiResponse(res))
		);
	}

	/**
	 * Get application URL.
	 */
	getApplicationUrl(): Observable<string> {
		return this.userRepo.getApplicationUrl().pipe(
			map(res => this.handleApiResponse(res))
		);
	}

	private getUserProfile(): Observable<UserProfile> {
		return this.userRepo.getUserProfile().pipe(
			map(res => this.handleApiResponse(res))
		);
	}

	private readonly profileFormMapper = (profile: UserProfile): ProfileForm => {
		return {
			firstName: profile.firstName,
			lastName: profile.lastName,
			email: profile.userName,
			dateOfBirth: new DateOnly(profile.birthDate),
			dateOfBirthFormat: convertToLocalDate(profile.birthDate),
			country: profile.country,
			hospital: this.isSurgeon(profile.roleInfo) ? profile.hospitalOrCompany : null,
			company: this.isSurgeon(profile.roleInfo) ? null : decodeURI(profile.hospitalOrCompany),
			distributor: profile.distributor ?? null,
			city: profile.city,
			address: decodeURI(profile.address),
			postalCode: profile.postalCode,
			officePhone: profile.officePhone,
			mobilePhone: profile.mobilePhone ?? null,
			stateProvince: this.isUnitedStates(profile.country) ? null : profile.stateProvince,
			usStateProvince: this.isUnitedStates(profile.country) ? profile.stateProvince : null,
			roleInfo: profile.roleInfo,
			practice: profile.practice ?? null
		};
	}

	private updateUserProfile(profile: UserProfile, form: ProfileForm): UserProfile {
		if (!form) return profile;
		return {
			...profile,
			birthDate: new DateOnly(form.dateOfBirth),
			hospitalOrCompany: this.isSurgeon(profile.roleInfo) ? form.hospital! : form.company!,
			distributor: this.isSurgeon(profile.roleInfo) ? form.distributor! : undefined,
			stateProvince: this.isUnitedStates(profile.country) ? form.usStateProvince! : form.stateProvince!,
			city: form.city,
			address: form.address,
			postalCode: form.postalCode,
			officePhone: form.officePhone,
			mobilePhone: form.mobilePhone ?? undefined
		};
	}

	private isUnitedStates(country: UserProfileCountry): boolean {
		return country?.code === US_CODE;
	}

	private isSurgeon(role: string): boolean {
		return !!role && role === SURGEON;
	}

	private handleApiResponse<T>(response: ApiResponse<T>): T {
		if (response.statusCode == ApiResponseStatusCode.Success) {
			return response.result as T;
		} else {
			throw new Error("Generic error");
		}
	}

}